import useAuth from '@app/auth/use-auth';
import { useUnitOptions } from '@app/ccf/company-report/utils';
import LifeCycleStageModelling from '@app/components/food-modelling/components/life-cycle-stage-modelling';
import { useSoldFoodModellingOptions } from '@app/components/food-modelling/food-modelling-options';
import {
  emptyEmissionFactors,
  formatEmissionFactors,
  getEmissionFactors,
} from '@app/components/food-modelling/utils';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import ModelledFood from '@app/purchased-goods/food-and-drink/components/modelled-food';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { StageEnum } from '@app/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import {
  useDeleteSoldFoodAndDrinkItem,
  usePatchSoldFoodAndDrinkItem,
} from '@shared/api/hooks/sold-goods/food-and-drink';
import {
  FoodOperationActivityDetail,
  PatchedFoodOperationActivityDetail,
  ProductIngredientEntityTypeEnum,
  SoldGoodModelledAsEnum,
} from '@shared/api/types';
import Heading from '@shared/components/content/heading';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import IngredientPicker from '@shared/components/pickers/ingredient-picker';
import { emptyBaseFoodRow } from '@shared/components/product-form/components/recipe-base-food-row';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import { formatSmallNumber } from '@shared/lib/utils';
import sentry from '@shared/services/sentry';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useSoldFoodAndDrinkItem } from '../../sold-food-and-drink-item-context';
import { soldItemFormSchema } from './sold-food-and-drink-item-form-schema';

interface SoldFoodAndDrinkItemFormProps {
  item: FoodOperationActivityDetail;
}

export default function SoldFoodAndDrinkItemForm({
  item,
}: SoldFoodAndDrinkItemFormProps) {
  const { user } = useAuth();
  const hasPurchasedGoodsAccess = user?.features.scope3;

  const { toast } = useToast();
  const navigate = useNavigate();
  const { referrer, refetchItem } = useSoldFoodAndDrinkItem();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    SimpleDialogComponent: deleteModal,
    closeSimpleDialog: closeDeleteModal,
    showSimpleDialog: showDeleteModal,
  } = useSimpleDialog();
  const { showIntercomArticle } = useIntercom();

  const { mutateAsync: updateFoodActivity, isLoading: isUpdatingFoodActivity } =
    usePatchSoldFoodAndDrinkItem();

  const { mutateAsync: deleteFoodActivity, isLoading: isDeletingFoodActivity } =
    useDeleteSoldFoodAndDrinkItem();

  const { data: units, isLoading: unitsIsLoading } =
    useGetActivityTypeUnits('food');
  const unitOptions = useUnitOptions(units!);

  const form = useForm<z.infer<typeof soldItemFormSchema>>({
    resolver: zodResolver(soldItemFormSchema),
  });

  const { reset } = form;

  useEffect(() => {
    const stages: Array<StageEnum> = [...Object.keys(StageEnum)] as StageEnum[];

    const emissionFactors =
      item.modelledAs === SoldGoodModelledAsEnum.basic_food &&
      getEmissionFactors(
        stages,
        item.soldGood.basicFood!.emissionFactors!,
        item.soldGood.overrideEmissionFactors
      );

    if (item) {
      reset({
        remoteId: item.remoteId || undefined,
        name: item.soldGood.name,
        inputUnit: item.inputUnit || undefined,
        modelledAs: item.modelledAs,
        unit: item.itemUnit?.uuid || undefined,
        itemsPerPack: item.itemsPerPack || undefined,
        itemWeight: item.itemMassOrVolume || undefined,
        recipeDescription: item.soldGood.name,
        basicFoodUuid: item.soldGood.basicFood?.uuid || undefined,
        linkedItemUuid:
          item.modelledAs === SoldGoodModelledAsEnum.product
            ? item.soldGood.product!.uuid
            : item.modelledAs === SoldGoodModelledAsEnum.purchased_good
              ? item.soldGood.purchasedGood!.uuid
              : '',

        ingredients:
          item.modelledAs === SoldGoodModelledAsEnum.modelled_food
            ? item.soldGood.modelledFood?.ingredients.map((ingredient) => ({
                entityUuid: ingredient.baseFood.uuid,
                name: ingredient.baseFood.name,
                entityType: 'base_food' as ProductIngredientEntityTypeEnum,
                quantity: ingredient.quantity,
                unitUuid: ingredient.unit.uuid,
                availableUnitTypes: [],
              }))
            : [emptyBaseFoodRow],

        emissionFactors:
          item.modelledAs === SoldGoodModelledAsEnum.basic_food
            ? emissionFactors
            : item.modelledAs === SoldGoodModelledAsEnum.product ||
                item.modelledAs === SoldGoodModelledAsEnum.modelled_food ||
                item.modelledAs === SoldGoodModelledAsEnum.purchased_good
              ? formatEmissionFactors(item.emissionFactors!)
              : item.modelledAs ===
                  SoldGoodModelledAsEnum.external_emission_factor
                ? item.soldGood.overrideEmissionFactors
                : undefined,
      });
    }
  }, [item, reset]);

  const onSubmit = async (data: z.infer<typeof soldItemFormSchema>) => {
    setIsSubmitting(true);
    const baseUpdatedData = {
      remoteId: data.remoteId,
      name: data.name,

      itemUnitUuid: data.unit,
      itemsPerPack: data.itemsPerPack,
      itemMassOrVolume: data.itemWeight,

      modelledAs: data.modelledAs,
    };

    const basicFoodUpdate = data.modelledAs ===
      SoldGoodModelledAsEnum.basic_food && {
      ...baseUpdatedData,
      basicFoodUuid: data.basicFoodUuid,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          if (factor?.method === 'custom') {
            acc[stage as StageEnum] = {
              value: factor?.value,
              source: factor?.source || null,
            };
          }
          return acc;
        },
        { ...emptyEmissionFactors } as Record<
          StageEnum,
          { value: number | null; source: string | null }
        >
      ),
    };

    const productUpdate = data.modelledAs ===
      SoldGoodModelledAsEnum.product && {
      ...baseUpdatedData,
      productUuid: data.linkedItemUuid,
    };

    const purchasedGoodUpdate = data.modelledAs ===
      SoldGoodModelledAsEnum.purchased_good && {
      ...baseUpdatedData,
      purchasedGoodUuid: data.linkedItemUuid,
    };

    const modelledFoodUpdate = data.modelledAs ===
      SoldGoodModelledAsEnum.modelled_food && {
      ...baseUpdatedData,
      modelledFood: {
        ingredients: data.ingredients?.map((ingredient) => ({
          baseFoodUuid: ingredient.entityUuid,
          quantity: ingredient.quantity,
          unitUuid: ingredient.unitUuid,
        })),
      },
    };

    const LCAUpdate = data.modelledAs ===
      SoldGoodModelledAsEnum.external_emission_factor && {
      ...baseUpdatedData,
      overrideEmissionFactors: Object.keys(data.emissionFactors!).reduce(
        (acc, stage) => {
          const factor = data.emissionFactors![stage as StageEnum];
          acc[stage as StageEnum] = {
            value: factor?.value,
            source: factor?.source || null,
          };
          return acc;
        },
        {} as Record<StageEnum, { value: number | null; source: string | null }>
      ),
    };

    const updateData =
      data.modelledAs === SoldGoodModelledAsEnum.external_emission_factor
        ? LCAUpdate
        : data.modelledAs === SoldGoodModelledAsEnum.basic_food
          ? basicFoodUpdate
          : data.modelledAs === SoldGoodModelledAsEnum.product
            ? productUpdate
            : data.modelledAs === SoldGoodModelledAsEnum.purchased_good
              ? purchasedGoodUpdate
              : modelledFoodUpdate;
    try {
      await updateFoodActivity({
        activityUuid: item.uuid,
        // updateData is one of the per-modelledAs shapes (never the `&&`
        // false branch at runtime); cast to the patch body the mutation expects.
        data: updateData as PatchedFoodOperationActivityDetail,
      });
      await refetchItem();
      reset();
      toast({
        title: 'Item updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update item',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFoodActivity({ activityUuid: item.uuid });
      toast({
        title: 'Item deleted',
        variant: 'success',
      });
      closeDeleteModal();
      referrer
        ? navigate(referrer)
        : navigate(generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK));
    } catch (error) {
      sentry.log(error);
    }
  };

  const modelledAs = form.watch('modelledAs');

  const hasPackConfiguration = !!item.itemUnit;

  return (
    <Form {...form}>
      <form
        className="mb-4 min-h-[360px] max-w-3xl gap-y-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <fieldset className="gap-4" disabled={isUpdatingFoodActivity}>
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-2">
              <InputFormField
                name="remoteId"
                disabled
                label="Your ID"
                className="col-span-2 max-w-fit"
              />
              <InputFormField
                name="name"
                disabled
                label="Food name"
                className="col-span-6"
              />
              {hasPackConfiguration && (
                <InputFormField
                  name="inputUnit"
                  options={unitOptions!}
                  className="col-span-3"
                  label="Original input unit"
                  disabled
                />
              )}
            </div>
            {hasPackConfiguration && (
              <div className="grid grid-cols-12 gap-3">
                <InputFormField
                  name="itemsPerPack"
                  label="Items per pack"
                  className="col-span-3"
                />
                <InputFormField
                  name="itemWeight"
                  label="Item weight"
                  className="col-span-3"
                />
                <SelectFormField
                  name="unit"
                  options={unitOptions!}
                  label="Unit"
                  className="col-span-2"
                  useShortLabel
                  loadingOptions={unitsIsLoading}
                />
              </div>
            )}
          </div>
          <div className="mt-4 space-y-4">
            <Text className="text-lg text-gray-600">Food modelling</Text>
            <div className="grid grid-cols-12 gap-2">
              <SelectFormField
                name="modelledAs"
                label="Modelled by"
                className="col-span-5"
                options={useSoldFoodModellingOptions()}
                includeErrorMessage
                showDescription
                showIcon
                handleSelect={(value) => {
                  if (
                    value === SoldGoodModelledAsEnum.external_emission_factor &&
                    item.modelledAs !==
                      SoldGoodModelledAsEnum.external_emission_factor
                  ) {
                    form.setValue('emissionFactors', emptyEmissionFactors);
                    return;
                  }

                  form.resetField('emissionFactors');
                }}
              />
              {modelledAs === SoldGoodModelledAsEnum.basic_food && (
                <IngredientPicker
                  name="basicFoodUuid"
                  include={['base_foods']}
                  label="Matched food"
                  className="col-span-5"
                  showLabel
                  knownSelectedItem={
                    item.soldGood.basicFood
                      ? {
                          uuid: item.soldGood.basicFood.uuid,
                          name: item.soldGood.basicFood.name,
                          type: 'base_food',
                          availableUnitTypes: [],
                        }
                      : undefined
                  }
                />
              )}
              {(modelledAs === SoldGoodModelledAsEnum.product ||
                modelledAs === SoldGoodModelledAsEnum.purchased_good) && (
                <div className="col-span-5 flex items-end gap-2 ">
                  <IngredientPicker
                    name="linkedItemUuid"
                    include={[
                      ...(user?.features.pcf ? (['products'] as const) : []),
                      ...(hasPurchasedGoodsAccess
                        ? (['purchased_goods'] as const)
                        : []),
                    ]}
                    label="Linked to"
                    className="col-span-5"
                    showLabel
                    onSelect={(selectedItem) => {
                      if (selectedItem.type === 'product') {
                        form.setValue(
                          'modelledAs',
                          SoldGoodModelledAsEnum.product
                        );
                      } else if (selectedItem.type === 'purchased_good') {
                        form.setValue(
                          'modelledAs',
                          SoldGoodModelledAsEnum.purchased_good
                        );
                      }
                    }}
                    knownSelectedItem={
                      item.soldGood.purchasedGood
                        ? {
                            uuid: item.soldGood.purchasedGood.uuid,
                            name: item.soldGood.purchasedGood.name,
                            type: 'purchased_good',
                            availableUnitTypes: [],
                          }
                        : item.soldGood.product
                          ? {
                              uuid: item.soldGood.product.uuid,
                              name: item.soldGood.product.name,
                              type: 'product',
                              availableUnitTypes: [],
                            }
                          : undefined
                    }
                  />
                  {item.modelledAs === SoldGoodModelledAsEnum.product && (
                    <Link
                      to={generatePath(pcfRoutes.PRODUCT_DETAIL, {
                        product_id: item.soldGood.product?.uuid,
                      })}
                    >
                      <Icons.linkExternal className="mb-3 size-5 text-teal" />
                    </Link>
                  )}
                  {item.modelledAs === SoldGoodModelledAsEnum.purchased_good &&
                    hasPurchasedGoodsAccess &&
                    item.soldGood.purchasedGood.purchasedGoodActivityUuid && (
                      <Link
                        to={generatePath(
                          purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM,
                          {
                            item_uuid:
                              item.soldGood.purchasedGood
                                ?.purchasedGoodActivityUuid,
                          }
                        )}
                      >
                        <Icons.linkExternal className="mb-3 size-5 text-teal" />
                      </Link>
                    )}
                </div>
              )}
            </div>

            {item.modelledAs === SoldGoodModelledAsEnum.basic_food &&
              modelledAs === SoldGoodModelledAsEnum.basic_food && (
                <div className="space-y-3 pt-1">
                  <Heading variant="subsection">Emission factors</Heading>
                  <Text variant="subtle">
                    Basic foods have four emissions factors, covering the
                    life-cycle stages from the farm up to your supplier. As
                    standard, we select the most up-to-date factors available
                    for your location. You can override the factors with values
                    from your supplier or another source. Please read our{' '}
                    <TextLink onClick={() => showIntercomArticle('customEFs')}>
                      guidelines for custom factors
                    </TextLink>{' '}
                    before doing so.
                  </Text>
                  <LifeCycleStageModelling
                    baseEmissionFactors={
                      item.soldGood.basicFood!.emissionFactors!
                    }
                    allowCustomValues
                    showCustomSelector
                  />
                </div>
              )}

            {(item.modelledAs === SoldGoodModelledAsEnum.product ||
              item.modelledAs === SoldGoodModelledAsEnum.purchased_good) &&
              (modelledAs === SoldGoodModelledAsEnum.product ||
                modelledAs === SoldGoodModelledAsEnum.purchased_good) && (
                <div className="space-y-3 pt-1">
                  <Heading variant="subsection">Emission factors</Heading>
                  <Text variant="subtle">
                    The emissions factors for this item are determined by the
                    modelling of the item it is linked to
                  </Text>
                  <LifeCycleStageModelling
                    baseEmissionFactors={formatEmissionFactors(
                      item.emissionFactors!
                    )}
                    showSourceColumn={false}
                  />
                </div>
              )}

            {modelledAs === SoldGoodModelledAsEnum.modelled_food && (
              <div className="mb-6 space-y-3 pt-1">
                <div className="flex items-center gap-4">
                  {item.soldGood.modelledFood && (
                    <div className="flex flex-col items-center">
                      <RatingCloud rating={item.emissionsCategory} />
                      <Text>{formatSmallNumber(item.emissionFactor)}</Text>
                      <Unit variant="kgC02e/kg" />
                    </div>
                  )}

                  {item.soldGood.modelledFood ? (
                    <Text>
                      The emissions for this item are calculated based on the
                      carbon intensity of the recipe below. A carbon intensity
                      of {formatSmallNumber(item.emissionFactor)} means that for
                      every 1 kg produced,{' '}
                      {formatSmallNumber(item.emissionFactor)} kg of carbon
                      dioxide equivalent is emitted.
                    </Text>
                  ) : (
                    <Text>
                      The emissions for modelled foods are determined by their
                      ingredients. You can either input a recipe below or use
                      our tool to generate one from the description.
                    </Text>
                  )}
                </div>
                <ModelledFood
                  item={item}
                  modelledFood={item.soldGood.modelledFood}
                  isSubmitting={isSubmitting}
                />
              </div>
            )}
            {modelledAs === SoldGoodModelledAsEnum.external_emission_factor && (
              <div className="space-y-3 pt-1">
                <Heading variant="subsection">Emission factors</Heading>
                <Text variant="subtle">
                  If you have completed a full product assessment with an
                  external provider or have the values from your supplier you
                  can enter them below. Please read our{' '}
                  <TextLink onClick={() => showIntercomArticle('customEFs')}>
                    guidelines for custom factors
                  </TextLink>{' '}
                  before doing so.
                </Text>
                <LifeCycleStageModelling
                  baseEmissionFactors={formatEmissionFactors(
                    item.emissionFactors!
                  )}
                  allowCustomValues
                  showSourceColumn
                />
              </div>
            )}
          </div>
          <div className=" mt-6 flex justify-between">
            <div className="col-span-4">
              <Button
                type="submit"
                disabled={isUpdatingFoodActivity}
                loading={isUpdatingFoodActivity}
              >
                Update sold item
              </Button>
            </div>
            <div className="col-span-4">
              <Button
                type="button"
                variant="destructive"
                onClick={showDeleteModal}
                disabled={isDeletingFoodActivity || isUpdatingFoodActivity}
              >
                Delete item
              </Button>
              {deleteModal({
                header: 'Delete sold item',
                content: (
                  <Text>
                    Deleting this item will also remove all related{' '}
                    <TextLink
                      to={generatePath(
                        soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM_SALES,
                        {
                          item_uuid: item.uuid,
                        }
                      )}
                    >
                      sales
                    </TextLink>{' '}
                    from your reports. This cannot be undone.
                  </Text>
                ),
                footer: (
                  <div className="space-x-2">
                    <Button
                      variant="outline"
                      onClick={closeDeleteModal}
                      disabled={isDeletingFoodActivity}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleDelete}
                      loading={isDeletingFoodActivity}
                    >
                      Delete item
                    </Button>
                  </div>
                ),
              })}
            </div>
          </div>
          <Text variant="subtle" className="mb-10 mt-4">
            Updating the modelling for this item will update all related{' '}
            <TextLink
              to={generatePath(soldGoodsRoutes.SOLD_FOOD_AND_DRINK_ITEM_SALES, {
                item_uuid: item.uuid,
              })}
            >
              sales
            </TextLink>
            , all reports will need to be republished.
          </Text>
        </fieldset>
      </form>
    </Form>
  );
}
